<script lang="ts">
  import type {
    ColorSeparateParams,
    GradientSepParams,
    GreyscaleParams,
    HalftoneParams,
    HalftonePattern,
  } from '$lib/model/types';
  import { projectStore, selectStep } from '$lib/state/project.svelte';
  import { addChild } from '$lib/model/tree';
  import {
    syncColorSeparateChildren,
    syncGradientSepChildren,
  } from '$lib/model/sync';
  import { triggerRecompute } from '$lib/compute/recompute.svelte';
  import { lpiCeiling } from '$lib/compute/settings-utils';
  import InputStepPanel from './InputStepPanel.svelte';
  import Slider from './Slider.svelte';
  import ColorPicker from './ColorPicker.svelte';

  const step = $derived(
    projectStore.selected_step_id
      ? (projectStore.pipeline.steps[projectStore.selected_step_id] ?? null)
      : null,
  );

  const lpiCap = $derived(lpiCeiling(projectStore.settings));

  function setHalftoneParam<K extends keyof HalftoneParams>(
    key: K,
    value: HalftoneParams[K],
  ) {
    if (!step) return;
    const p = step.params as HalftoneParams;
    p[key] = value;
    // Keep min_dot < max_dot (with a 1pt buffer).
    if (key === 'min_dot_pct' && p.min_dot_pct >= p.max_dot_pct) {
      p.max_dot_pct = Math.min(100, p.min_dot_pct + 1);
    } else if (key === 'max_dot_pct' && p.max_dot_pct <= p.min_dot_pct) {
      p.min_dot_pct = Math.max(0, p.max_dot_pct - 1);
    }
    triggerRecompute();
  }

  function setChannelField(
    idx: number,
    field: 'name' | 'target' | 'threshold',
    value: string | number,
  ) {
    if (!step) return;
    const p = step.params as ColorSeparateParams;
    const ch = p.channels[idx];
    if (!ch) return;
    if (field === 'name') ch.name = value as string;
    else if (field === 'target') ch.target = value as string;
    else ch.threshold = value as number;
    syncColorSeparateChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
  function addChannel() {
    if (!step) return;
    const p = step.params as ColorSeparateParams;
    const name = `Channel ${p.channels.length + 1}`;
    p.channels = [
      ...p.channels,
      { name, target: '#888888', threshold: 0 },
    ];
    syncColorSeparateChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
  function removeChannel(idx: number) {
    if (!step) return;
    const p = step.params as ColorSeparateParams;
    p.channels = p.channels.filter((_, i) => i !== idx);
    syncColorSeparateChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
  function addHalftoneToAllChannels() {
    if (!step) return;
    for (const channelId of [...step.children]) {
      const ch = projectStore.pipeline.steps[channelId];
      if (!ch || ch.type !== 'channel') continue;
      // Skip channels that already have a halftone child.
      const hasHalftone = ch.children.some((cid) => {
        const c = projectStore.pipeline.steps[cid];
        return c?.type === 'halftone';
      });
      if (hasHalftone) continue;
      addChild(projectStore.pipeline, channelId, 'halftone', {
        inheritNameFromParent: true,
      });
    }
    triggerRecompute();
  }

  function moveChannel(idx: number, delta: -1 | 1) {
    if (!step) return;
    const p = step.params as ColorSeparateParams;
    const ni = idx + delta;
    if (ni < 0 || ni >= p.channels.length) return;
    const next = [...p.channels];
    const [moved] = next.splice(idx, 1);
    if (!moved) return;
    next.splice(ni, 0, moved);
    p.channels = next;
    syncColorSeparateChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }

  // Gradient bands are a partition of [0, 1]. Editing one boundary moves the
  // corresponding adjacent band's boundary in lockstep.
  function setBandName(idx: number, value: string) {
    if (!step) return;
    const p = step.params as GradientSepParams;
    const b = p.bands[idx];
    if (!b) return;
    b.name = value;
    syncGradientSepChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
  // The single editable boundary per band is its `max`. The next band's
  // `min` is kept in lockstep.
  function setBandBoundary(idx: number, value: number) {
    if (!step) return;
    const p = step.params as GradientSepParams;
    if (idx >= p.bands.length - 1) return; // last band has fixed max=1
    const band = p.bands[idx];
    const next = p.bands[idx + 1];
    if (!band || !next) return;
    const lo = band.min + 0.001;
    const hi = next.max - 0.001;
    const clamped = Math.max(lo, Math.min(hi, value));
    band.max = clamped;
    next.min = clamped;
    syncGradientSepChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
  // Suppress unused-import lint
  void selectStep;

  function addBand() {
    if (!step) return;
    const p = step.params as GradientSepParams;
    if (p.bands.length === 0) {
      p.bands = [{ name: 'Band 1', min: 0, max: 1 }];
    } else {
      // Split the last band in half.
      const last = p.bands[p.bands.length - 1];
      if (!last) return;
      const mid = (last.min + last.max) / 2;
      const oldMax = last.max;
      last.max = mid;
      p.bands = [
        ...p.bands,
        { name: `Band ${p.bands.length + 1}`, min: mid, max: oldMax },
      ];
    }
    syncGradientSepChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
  function removeBand(idx: number) {
    if (!step) return;
    const p = step.params as GradientSepParams;
    if (p.bands.length <= 1) return;
    const removed = p.bands[idx];
    if (!removed) return;
    p.bands = p.bands.filter((_, i) => i !== idx);
    // Stitch the gap: the band that previously followed the removed band
    // takes over its range start; the band before takes over its range end.
    if (idx > 0 && idx < p.bands.length) {
      const before = p.bands[idx - 1];
      const after = p.bands[idx];
      if (before && after) {
        const mid = (before.max + after.min) / 2;
        before.max = mid;
        after.min = mid;
      }
    } else if (idx === 0 && p.bands.length > 0) {
      p.bands[0]!.min = 0;
    } else if (idx === p.bands.length && p.bands.length > 0) {
      p.bands[p.bands.length - 1]!.max = 1;
    }
    syncGradientSepChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
  function moveBand(idx: number, delta: -1 | 1) {
    if (!step) return;
    const p = step.params as GradientSepParams;
    const ni = idx + delta;
    if (ni < 0 || ni >= p.bands.length) return;
    const next = [...p.bands];
    const [moved] = next.splice(idx, 1);
    if (!moved) return;
    next.splice(ni, 0, moved);
    // After reordering by user intent, re-sort ranges so they form a
    // partition: reassign min/max to evenly span the original total range,
    // preserving relative widths.
    p.bands = next;
    syncGradientSepChildren(projectStore.pipeline, step.id);
    triggerRecompute();
  }
</script>

{#if !step}
  <p class="empty">Select a step to edit its parameters.</p>
{:else if step.type === 'input'}
  <InputStepPanel />
{:else if step.type === 'greyscale'}
  <section class="panel">
    <h3>{step.name}</h3>
    <p class="hint">
      Channel weights for luma. Defaults match Rec.601. Weights are
      normalized on use, so absolute values don't matter — only their ratio.
    </p>
    {#snippet gp(p: GreyscaleParams)}
      <Slider
        label="Red weight"
        value={p.r_weight}
        min={0}
        max={1}
        step={0.01}
        digits={2}
        onchange={(v) => {
          (step!.params as GreyscaleParams).r_weight = v;
          triggerRecompute();
        }}
      />
      <Slider
        label="Green weight"
        value={p.g_weight}
        min={0}
        max={1}
        step={0.01}
        digits={2}
        onchange={(v) => {
          (step!.params as GreyscaleParams).g_weight = v;
          triggerRecompute();
        }}
      />
      <Slider
        label="Blue weight"
        value={p.b_weight}
        min={0}
        max={1}
        step={0.01}
        digits={2}
        onchange={(v) => {
          (step!.params as GreyscaleParams).b_weight = v;
          triggerRecompute();
        }}
      />
      <button
        class="reset"
        onclick={() => {
          const params = step!.params as GreyscaleParams;
          params.r_weight = 0.299;
          params.g_weight = 0.587;
          params.b_weight = 0.114;
          triggerRecompute();
        }}>Reset to Rec.601</button
      >
    {/snippet}
    {@render gp(step.params as GreyscaleParams)}
  </section>
{:else if step.type === 'color_separate'}
  <section class="panel">
    <h3>{step.name}</h3>
    <p class="hint">
      Each pixel is classified into its closest target channel. Channel output
      is a binary mask — directly exportable. Adjust threshold to filter out
      poor matches.
    </p>
    <div class="rows">
      {#each (step.params as ColorSeparateParams).channels as ch, idx (idx)}
        <div class="chan-block">
          <div class="chan-head">
            <ColorPicker
              value={ch.target}
              onchange={(v) => setChannelField(idx, 'target', v)}
              ariaLabel="Channel target color"
            />
            <input
              class="chan-name"
              type="text"
              value={ch.name}
              oninput={(e) =>
                setChannelField(
                  idx,
                  'name',
                  (e.target as HTMLInputElement).value,
                )}
            />
            <button
              class="ico"
              onclick={() => moveChannel(idx, -1)}
              aria-label="Move channel up">↑</button
            >
            <button
              class="ico"
              onclick={() => moveChannel(idx, 1)}
              aria-label="Move channel down">↓</button
            >
            <button
              class="ico"
              onclick={() => removeChannel(idx)}
              aria-label="Remove channel">×</button
            >
          </div>
          <Slider
            label="Threshold (0 = include all, 1 = strict)"
            value={ch.threshold}
            min={0}
            max={1}
            step={0.05}
            digits={2}
            onchange={(v) => setChannelField(idx, 'threshold', v)}
          />
        </div>
      {/each}
    </div>
    <div class="row-actions">
      <button class="add" onclick={addChannel}>+ Add channel</button>
      <button class="add" onclick={addHalftoneToAllChannels}>
        Halftone all channels
      </button>
    </div>
  </section>
{:else if step.type === 'channel'}
  <section class="panel">
    <h3>{step.name}</h3>
    <p class="hint">
      Channel #{(step.params as { channel_index: number }).channel_index + 1}
      of its color separation. Output is already a binary mask and exportable.
      Optionally attach a halftone child for a stylized effect on the mask.
    </p>
  </section>
{:else if step.type === 'gradient_sep'}
  <section class="panel">
    <h3>{step.name}</h3>
    <p class="hint">
      Bands partition tones 0 (black) → 1 (white). The right edge of each
      band is the boundary shared with the next band.
    </p>
    <div class="rows">
      {#each (step.params as GradientSepParams).bands as b, idx (idx)}
        {@const allBands = (step.params as GradientSepParams).bands}
        {@const last = idx === allBands.length - 1}
        {@const prev = allBands[idx - 1]}
        {@const next = allBands[idx + 1]}
        {@const sliderMin = (prev?.max ?? 0) + 0.01}
        {@const sliderMax = (next?.max ?? 1) - 0.01}
        <div class="band-block">
          <div class="band-head">
            <input
              class="band-name"
              type="text"
              value={b.name}
              oninput={(e) =>
                setBandName(idx, (e.target as HTMLInputElement).value)}
            />
            <code class="range">{b.min.toFixed(2)}–{b.max.toFixed(2)}</code>
            <button
              class="ico"
              onclick={() => moveBand(idx, -1)}
              aria-label="Move band up">↑</button
            >
            <button
              class="ico"
              onclick={() => moveBand(idx, 1)}
              aria-label="Move band down">↓</button
            >
            <button
              class="ico"
              onclick={() => removeBand(idx)}
              aria-label="Remove band">×</button
            >
          </div>
          {#if !last}
            <Slider
              label="Ends at"
              value={b.max}
              min={sliderMin}
              max={sliderMax}
              step={0.01}
              digits={2}
              onchange={(v) => setBandBoundary(idx, v)}
            />
          {/if}
        </div>
      {/each}
    </div>
    <button class="add" onclick={addBand}>+ Add band</button>
  </section>
{:else if step.type === 'band'}
  <section class="panel">
    <h3>{step.name}</h3>
    <p class="hint">
      Band #{(step.params as { band_index: number }).band_index + 1}. This
      step's output is already binary and exportable.
    </p>
  </section>
{:else if step.type === 'halftone'}
  <section class="panel">
    <h3>{step.name}</h3>
    {#snippet hp(p: HalftoneParams)}
      <label class="select-row">
        <span>Pattern</span>
        <select
          value={p.pattern}
          onchange={(e) =>
            setHalftoneParam(
              'pattern',
              (e.target as HTMLSelectElement).value as HalftonePattern,
            )}
        >
          <option value="dot">dot</option>
        </select>
      </label>
      <Slider
        label="LPI (ceiling {lpiCap})"
        value={p.lpi}
        min={10}
        max={Math.max(lpiCap + 20, 100)}
        step={1}
        onchange={(v) => setHalftoneParam('lpi', v)}
      />
      <Slider
        label="Angle"
        value={p.angle}
        min={0}
        max={90}
        step={1}
        unit="°"
        onchange={(v) => setHalftoneParam('angle', v)}
      />
      <Slider
        label="Gamma"
        value={p.gamma}
        min={0.2}
        max={3.0}
        step={0.05}
        digits={2}
        onchange={(v) => setHalftoneParam('gamma', v)}
      />
      <Slider
        label="Min dot"
        value={p.min_dot_pct}
        min={0}
        max={50}
        step={1}
        unit="%"
        onchange={(v) => setHalftoneParam('min_dot_pct', v)}
      />
      <Slider
        label="Max dot"
        value={p.max_dot_pct}
        min={50}
        max={100}
        step={1}
        unit="%"
        onchange={(v) => setHalftoneParam('max_dot_pct', v)}
      />
      {#if p.lpi > lpiCap}
        <p class="warn-text">
          LPI {p.lpi} exceeds ceiling {lpiCap} — print may not hold dots.
        </p>
      {/if}
    {/snippet}
    {@render hp(step.params as HalftoneParams)}
  </section>
{/if}

<style>
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--gap-m);
  }
  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-dim);
  }
  .empty {
    color: var(--text-dim);
    font-size: 13px;
    margin: 0;
  }
  .hint {
    color: var(--text-dim);
    font-size: 12px;
    margin: 0;
  }
  .rows {
    display: flex;
    flex-direction: column;
    gap: var(--gap-m);
  }
  .chan-block,
  .band-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px;
    background: var(--surface-2);
    border-radius: 3px;
  }
  .chan-head,
  .band-head {
    display: grid;
    align-items: center;
    gap: 6px;
  }
  .chan-head {
    grid-template-columns: 32px 1fr 22px 22px 22px;
  }
  .band-head {
    grid-template-columns: 1fr auto 22px 22px 22px;
  }
  .chan-name,
  .band-name {
    font-size: 12px;
  }
  .range {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
  }
  .ico {
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-dim);
    font-size: 13px;
    line-height: 1;
    padding: 0;
    height: 22px;
  }
  .ico:hover {
    color: var(--accent);
    border-color: var(--accent);
  }
  .add,
  .reset {
    align-self: flex-start;
    font-size: 12px;
  }
  .row-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .row-actions .add {
    align-self: auto;
  }
  .select-row {
    display: flex;
    align-items: center;
    gap: var(--gap-s);
    font-size: 12px;
    color: var(--text-dim);
  }
  .select-row select {
    font-size: 12px;
  }
  .warn-text {
    color: var(--warn);
    font-size: 12px;
    margin: 0;
  }
</style>
